const LOCALE_KEY_PATTERN = /^[a-z]{2,3}(?:[-_][a-z0-9]+)*$/i;
const WELCOME_TITLE_SELECTION_SEED = Math.random().toString(36).slice(2);

const normalizeRawValue = rawValue => (rawValue || '').toString();
const normalizeTitleOptions = title => {
  if (typeof title === 'string') {
    const normalizedTitle = title.trim();
    return normalizedTitle ? [normalizedTitle] : [];
  }

  if (Array.isArray(title)) {
    return title.reduce((result, item) => {
      if (typeof item !== 'string') {
        return result;
      }

      const normalizedItem = item.trim();
      if (normalizedItem) {
        result.push(normalizedItem);
      }

      return result;
    }, []);
  }

  return [];
};

const isValidTitleValue = title => {
  if (typeof title === 'string') {
    return title.trim().length > 0;
  }

  if (Array.isArray(title)) {
    return (
      title.length > 0 &&
      title.every(item => typeof item === 'string' && item.trim().length > 0)
    );
  }

  return false;
};

const getTitleSelectionIndex = ({ locale, options, selectionSeed }) => {
  if (options.length === 1) {
    return 0;
  }

  const hashInput = `${selectionSeed}:${locale}:${options.join('|||')}`;
  let hash = 0;

  for (let index = 0; index < hashInput.length; index += 1) {
    hash = (hash * 31 + hashInput.charCodeAt(index)) % 2147483647;
  }

  return hash % options.length;
};

export const normalizeLocale = locale =>
  (locale || '').toString().trim().replace(/_/g, '-').toLowerCase();

export const normalizeWelcomeTitleLocales = locales => {
  if (!locales || typeof locales !== 'object' || Array.isArray(locales)) {
    return {};
  }

  return Object.entries(locales).reduce((result, [locale, title]) => {
    const normalizedLocale = normalizeLocale(locale);
    const normalizedTitleOptions = normalizeTitleOptions(title);

    if (
      normalizedLocale &&
      LOCALE_KEY_PATTERN.test(normalizedLocale) &&
      normalizedTitleOptions.length
    ) {
      result[normalizedLocale] = normalizedTitleOptions;
    }

    return result;
  }, {});
};

export const parseWelcomeTitleLocales = rawValue => {
  if (!rawValue || !rawValue.toString().trim()) {
    return {};
  }

  try {
    return normalizeWelcomeTitleLocales(JSON.parse(rawValue));
  } catch {
    return {};
  }
};

export const extractLegacyWelcomeTitles = rawValue =>
  normalizeRawValue(rawValue)
    .split('|||')
    .map(title => title.trim())
    .filter(Boolean);

export const isValidWelcomeTitleLocalesJson = rawValue => {
  if (!rawValue || !rawValue.toString().trim()) {
    return true;
  }

  try {
    const parsedValue = JSON.parse(rawValue);

    if (
      !parsedValue ||
      typeof parsedValue !== 'object' ||
      Array.isArray(parsedValue)
    ) {
      return false;
    }

    const entries = Object.entries(parsedValue);

    return (
      entries.length > 0 &&
      entries.every(([locale, title]) => {
        const normalizedLocale = normalizeLocale(locale);
        return (
          LOCALE_KEY_PATTERN.test(normalizedLocale) && isValidTitleValue(title)
        );
      })
    );
  } catch {
    return false;
  }
};

export const isValidWelcomeTitleInput = ({
  rawValue,
  persistedValue = '',
  allowUnchangedLegacy = false,
}) => {
  if (isValidWelcomeTitleLocalesJson(rawValue)) {
    return true;
  }

  if (!allowUnchangedLegacy) {
    return false;
  }

  const normalizedRawValue = normalizeRawValue(rawValue);
  const normalizedPersistedValue = normalizeRawValue(persistedValue);

  return (
    normalizedRawValue === normalizedPersistedValue &&
    normalizedPersistedValue.trim().length > 0 &&
    !isValidWelcomeTitleLocalesJson(normalizedPersistedValue) &&
    extractLegacyWelcomeTitles(normalizedPersistedValue).length > 0
  );
};

export const getWelcomeTitleLocalesForInput = ({
  rawValue,
  persistedValue = '',
  allowUnchangedLegacy = false,
}) => {
  const parsedLocales = parseWelcomeTitleLocales(rawValue);

  if (Object.keys(parsedLocales).length > 0) {
    return parsedLocales;
  }

  if (!allowUnchangedLegacy) {
    return {};
  }

  const normalizedRawValue = normalizeRawValue(rawValue);
  const normalizedPersistedValue = normalizeRawValue(persistedValue);

  if (normalizedRawValue !== normalizedPersistedValue) {
    return {};
  }

  const legacyTitles = extractLegacyWelcomeTitles(normalizedPersistedValue);
  return legacyTitles.length ? { en: legacyTitles } : {};
};

export const resolveWelcomeTitleOptions = ({
  locale = '',
  welcomeTitleLocales = {},
}) => {
  const normalizedLocales =
    typeof welcomeTitleLocales === 'string'
      ? parseWelcomeTitleLocales(welcomeTitleLocales)
      : normalizeWelcomeTitleLocales(welcomeTitleLocales);

  const normalizedLocale = normalizeLocale(locale);
  const fallbackLocales = [];

  if (normalizedLocale) {
    fallbackLocales.push(normalizedLocale);

    const baseLocale = normalizedLocale.split('-')[0];
    if (baseLocale !== normalizedLocale) {
      fallbackLocales.push(baseLocale);
    }
  }

  if (!fallbackLocales.includes('en')) {
    fallbackLocales.push('en');
  }

  return (
    fallbackLocales
      .map(candidate => normalizedLocales[candidate])
      .find(options => options?.length) ||
    Object.values(normalizedLocales).find(options => options?.length) ||
    []
  );
};

export const resolveWelcomeTitle = ({
  appConfigTitle = '',
  locale = '',
  welcomeTitleLocales = {},
  websiteName = '',
  fallbackToWebsiteName = true,
  selectionSeed = WELCOME_TITLE_SELECTION_SEED,
}) => {
  if (appConfigTitle && appConfigTitle.trim()) {
    return appConfigTitle;
  }

  const resolvedOptions = resolveWelcomeTitleOptions({
    locale,
    welcomeTitleLocales,
  });

  if (resolvedOptions.length) {
    return resolvedOptions[
      getTitleSelectionIndex({
        locale: normalizeLocale(locale),
        options: resolvedOptions,
        selectionSeed,
      })
    ];
  }

  return fallbackToWebsiteName ? websiteName || '' : '';
};
